import React from 'react';
import { includes, pathOr } from 'ramda';
import { string, func, shape, object } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import classNames from 'classnames';

import { NamedLink, ResponsiveImage, SuperHost } from '../../components';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import {
  DAYS_CONSIDERED_NEW_LISTING,
  getDaysSinceCreation,
  getDistanceBetweenCities,
  getLatLngFromOrigin,
  MIN_REVIEWS_TO_SHOW_AVERAGE_RATE,
} from '../../util/listingHelpers';
import config from '../../config';
import defaultListingImg from '../../assets/default-img-listing/default-img-listing.jpg';
import defaultListingImgCrop from '../../assets/default-img-listing/landscape-crop.webp';
import defaultListingImgCrop2 from '../../assets/default-img-listing/landscape-cropx2.webp';
import ratingImg from '../../assets/star.svg';
import css from './ListingCard.css';

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency },
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency },
      ),
    };
  }
  return {};
};

export const ListingCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    search,
    setActiveListing,
    to,
    myFavorites = [],
    handleAddToMyFavorites,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price } = currentListing.attributes;
  const slug = createSlug(title);
  const isInMyFavorites = includes(listing.id.uuid, myFavorites);
  const isHostedBySuperHost = pathOr(false, ['attributes', 'publicData', 'isHostedBySuperHost'], listing);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const hasReviewsInfo = pathOr(false, ['attributes', 'publicData', 'reviewsInfo' ], currentListing);
  const totalReviews = pathOr(0, ['attributes', 'publicData', 'reviewsInfo', 'totalReviews' ], currentListing);
  const hasReviews = totalReviews > 0;
  const hasEnoughReviewsToAverage = totalReviews >= MIN_REVIEWS_TO_SHOW_AVERAGE_RATE;
  const averageRating = pathOr(0, ['attributes', 'publicData', 'reviewsInfo', 'averageRating' ], currentListing);
  const isNewListing = getDaysSinceCreation(currentListing.attributes.createdAt) < DAYS_CONSIDERED_NEW_LISTING && !hasEnoughReviewsToAverage;
  const reviewMessage = isNewListing && totalReviews === 0
    ? (<FormattedMessage id="ListingCard.newListing" />)
    : hasReviews
    ? (<FormattedMessage id="ListingCard.totalReviews" values={{ totalReviews }} />)
    : (<FormattedMessage id="ListingCard.noReviews" />);
  const searchLatLng = search && getLatLngFromOrigin(search.origin);
  const cityGeoLocation = pathOr(null, ['attributes', 'geolocation'], currentListing);
  const cityLatLng = cityGeoLocation &&  {
    latitude: cityGeoLocation.lat,
    longitude: cityGeoLocation.lng,
  };
  const distanceFromSearch = searchLatLng && cityLatLng && getDistanceBetweenCities(searchLatLng, cityLatLng, intl.locale)
  const searchCity = search && search.city;
  const city = pathOr(null, ['attributes', 'publicData', 'location', 'city'], currentListing);
  const distanceMessage = distanceFromSearch && searchCity !== null && city !== searchCity ?
    <FormattedMessage id="ListingCard.differentCityDistance" values={{ distance: distanceFromSearch, city: searchCity }} /> :
    distanceFromSearch && distanceFromSearch !== 0 ?
    <FormattedMessage id="ListingCard.sameCityDistance" values={{ distance: distanceFromSearch }} /> :
    null;
  const defaultListingImgVariants = `${defaultListingImgCrop} 400w, ${defaultListingImgCrop2} 800w`;
  const { formattedPrice, priceTitle } = priceData(price, intl);

  return (
    <div className={classes}>
      <NamedLink to={to} name="ListingPage" params={{ id, slug }}>
        <div
          className={css.threeToTwoWrapper}
          onMouseEnter={() => setActiveListing(currentListing.id)}
          onMouseLeave={() => setActiveListing(null)}
        >
          <div className={css.aspectWrapper}>
            {firstImage === null
              ?
              <img src={defaultListingImg} alt={title} className={css.rootForImage} srcSet={defaultListingImgVariants}/>
              : <ResponsiveImage
                rootClassName={css.rootForImage}
                alt={title}
                image={firstImage}
                variants={['landscape-crop', 'landscape-crop2x']}
                sizes={renderSizes}
              />
            }
          </div>
        </div>
      </NamedLink>
      <NamedLink className={css.link} to={to} name="ListingPage" params={{ id, slug }}>
        <div className={css.info}>
          {hasReviewsInfo && (
            <div className={css.reviewsInfo}>
              <img className={css.ratingImg} src={ratingImg}/>
              {hasEnoughReviewsToAverage && <div className={css.averageRating}>{averageRating}</div>}
              <div className={css.totalReviews}>
                {reviewMessage}
              </div>
            </div>
          )}
          <div className={css.title}> {title}</div>
          <div className={css.locationInfo}>
            <div className={css.city}>{city}</div>
            {searchCity && city && distanceMessage && <span className={css.separator}>•</span>}
            {searchCity && distanceMessage && <div className={css.distance}>{distanceMessage}</div>}
          </div>
          <div className={css.priceInfo}>
            <div className={css.priceValue} title={priceTitle}>{formattedPrice}</div>
            <div className={css.perUnit}><FormattedMessage id="ListingCard.perUnit"/></div>
          </div>
        </div>
        <div className={css.alignTitleAndFavBtn}>
          {handleAddToMyFavorites && (
            <div className={css.buttonFavoriteMobileContainer}>
              <button
                className={isInMyFavorites ? css.removeFromMyFavoritesButton : css.addToMyFavoritesButton}
                onClick={(e) => handleAddToMyFavorites(e, listing.id)}
              />
            </div>
          )}
          {isHostedBySuperHost && (<SuperHost rootClassName={css.superHostContainer}/>)}
        </div>
      </NamedLink>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  search: null,
  to: null,
  setActiveListing: () => null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  search: object,
  to: shape({ search: string }),

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);



// WEBPACK FOOTER //
// ./src/components/ListingCard/ListingCard.js